import {
  GET_BUCKET_TEMPLATES,
  CREATE_BUCKET_TEMPLATE,
  UPDATE_BUCKET_TEMPLATE,
  DELETE_BUCKET_TEMPLATE,
  GET_BUCKETS,
  CREATE_BUCKET,
  UPDATE_BUCKET,
  DELETE_BUCKET,
  GET_BUCKET_NOTES,
  ADD_BUCKET_NOTE,
  GET_BUCKET_CUSTOM_DATA,
  GET_BUCKETS_CUSTOM_DATA,
  GET_BUCKET_OVERRIDES,
  GET_MY_BUCKET_TAGS,
  MARK_BUCKET_TAGS_AS_READ,
  NEW_BUCKET_TAGS_FROM_SOCKET,
  GET_BUCKET_TEMPLATE_STATUSES,
  UPDATE_BUCKET_STATUSES,
} from '../../state/actionTypes';

const createBucketTagCountMap = (tags) => {
  const countMap = {};
  tags.forEach((tag) => {
    const { bucketId } = tag;
    countMap[bucketId] = (countMap[bucketId] || 0) + 1;
  });
  return countMap;
};

const initialState = {
  buckets: [],
  bucketTemplates: [],
  bucketNotes: {},
  bucketTemplateMap: {},
  customData: [],
  customDataFiles: {},
  overrides: {},
  myBucketTags: [],
  myReadBucketTags: [],
  bucketTagsPerBucket: {},
  statuses: {},
};

export default (state = initialState, action) => {
  switch (action.type) {
    case GET_BUCKET_TEMPLATES:
    case CREATE_BUCKET_TEMPLATE:
    case UPDATE_BUCKET_TEMPLATE:
    case DELETE_BUCKET_TEMPLATE:
      return {
        ...state,
        bucketTemplates: action.payload,
      };
    case GET_BUCKETS: {
      const {
        payload: {
          query = {},
          data: buckets = [],
        } = {},
      } = action;

      const { bucketTemplateMap = {} } = state;

      if (query.bucketTemplateIds) {
        const newBucketTemplateMap = { ...bucketTemplateMap };

        query.bucketTemplateIds.forEach((bucketTemplateId) => {
          newBucketTemplateMap[bucketTemplateId] = buckets.filter(
            (bucket) => bucket.bucketTemplateId === bucketTemplateId,
          );
        });
        return {
          ...state,
          bucketTemplateMap: newBucketTemplateMap,
        };
      }

      return {
        ...state,
        buckets,
      };
    }
    case CREATE_BUCKET:
    case UPDATE_BUCKET:
    case DELETE_BUCKET: {
      const { create, update, delete: deletePayload } = action.payload;

      let newBuckets = [...state.buckets];

      if (create) {
        newBuckets.push(create);
      }

      if (update) {
        newBuckets = newBuckets.map((bucket) => {
          if (!update[bucket.id]) return bucket;
          return {
            ...bucket,
            ...update[bucket.id],
          };
        });
      }

      if (deletePayload) {
        newBuckets = newBuckets.filter((bucket) => !deletePayload[bucket.id]);
      }

      return {
        ...state,
        buckets: newBuckets,
      };
    }
    case UPDATE_BUCKET_STATUSES: {
      const {
        payload: {
          data = {},
        } = {},
      } = action;

      let newBuckets = [...state.buckets];
      newBuckets = newBuckets.map((bucket) => {
        if (!data[bucket.id]) return bucket;
        return {
          ...bucket,
          statusId: data[bucket.id].statusId,
        };
      });

      return {
        ...state,
        buckets: newBuckets,
      };
    }
    case GET_BUCKET_NOTES: {
      const {
        payload: {
          id,
          data: notes = [],
        } = {},
      } = action;
      const { bucketNotes: stateNotes = {} } = state;
      const existingNotes = { ...stateNotes };
      existingNotes[id] = notes;
      existingNotes[id].sort((a, b) => b.timestamp - a.timestamp);
      return {
        ...state,
        bucketNotes: existingNotes,
      };
    }
    case ADD_BUCKET_NOTE: {
      const {
        payload = {},
      } = action;
      const { bucketId } = payload;
      const { bucketNotes: stateNotes = {} } = state;
      const existingNotes = { ...stateNotes };
      const {
        [bucketId]: existingUserNotes = [],
      } = existingNotes;
      existingNotes[bucketId] = existingUserNotes.concat([payload]);
      existingNotes[bucketId].sort((a, b) => b.timestamp - a.timestamp);
      return {
        ...state,
        bucketNotes: existingNotes,
      };
    }
    case GET_BUCKET_CUSTOM_DATA: {
      const {
        data: {
          data = [],
          fileMap = {},
        } = {},
      } = action.payload;
      return {
        ...state,
        customData: data,
        customDataFiles: fileMap,
      };
    }
    case GET_BUCKETS_CUSTOM_DATA: {
      const {
        payload: {
          ids = [],
          customDataMap = {},
        } = {},
      } = action;

      // Buckets with no saved custom data won't have an entry in customDataMap. We still
      // need to mark them as "fetched" with a truthy value so useOnDemandBucketCustomData
      // doesn't keep re-requesting them on every buckets state change.
      const requestedIdSet = new Set(ids);
      const mergeCustomData = (bucket) => {
        if (customDataMap[bucket.id]) return { ...bucket, customData: customDataMap[bucket.id] };
        if (requestedIdSet.has(bucket.id) && !bucket.customData) {
          return { ...bucket, customData: { sections: [], fields: [] } };
        }
        return bucket;
      };

      const { bucketTemplateMap = {} } = state;
      const newBucketTemplateMap = {};
      Object.keys(bucketTemplateMap).forEach((bucketTemplateId) => {
        newBucketTemplateMap[bucketTemplateId] = bucketTemplateMap[bucketTemplateId]
          .map(mergeCustomData);
      });

      return {
        ...state,
        buckets: state.buckets.map(mergeCustomData),
        bucketTemplateMap: newBucketTemplateMap,
      };
    }
    case GET_BUCKET_OVERRIDES: {
      const {
        payload: {
          data = {},
        } = {},
      } = action;
      return {
        ...state,
        overrides: data,
      };
    }
    case GET_MY_BUCKET_TAGS: {
      const {
        payload: {
          myBucketTags = [],
          hasRead,
        } = {},
      } = action;
      if (hasRead) {
        return {
          ...state,
          myReadBucketTags: myBucketTags,
          bucketTagsPerBucket: createBucketTagCountMap(myBucketTags),
        };
      }
      return {
        ...state,
        myBucketTags,
        bucketTagsPerBucket: createBucketTagCountMap(myBucketTags),
      };
    }
    case MARK_BUCKET_TAGS_AS_READ: {
      const {
        payload: {
          noteIds = new Set(),
        } = {},
      } = action;
      const { myBucketTags: stateBucketTags = [] } = state;
      const filteredTags = stateBucketTags.filter((tag) => (
        !noteIds.has(tag.bucketNoteId)
      ));
      return {
        ...state,
        myBucketTags: filteredTags,
        bucketTagsPerBucket: createBucketTagCountMap(filteredTags),
      };
    }
    case NEW_BUCKET_TAGS_FROM_SOCKET: {
      const {
        payload: {
          newTags = [],
        } = {},
      } = action;
      const { myBucketTags: stateBucketTags = [] } = state;
      const existingSet = new Set(stateBucketTags.map((tag) => tag.id));
      const fullTags = stateBucketTags.concat(
        newTags.filter((tag) => !existingSet.has(tag.id)),
      );
      return {
        ...state,
        myBucketTags: fullTags,
        bucketTagsPerBucket: createBucketTagCountMap(fullTags),
      };
    }
    case GET_BUCKET_TEMPLATE_STATUSES: {
      const {
        payload: {
          data = [],
          templateId,
        } = {},
      } = action;
      return {
        ...state,
        statuses: {
          ...state.statuses,
          [templateId]: data,
        },
      };
    }
    default:
      return state;
  }
};
